import React, { useEffect, useState } from 'react';
import { Loader2, Save, Plus, Trash2, BarChart3, Sparkles, ShieldCheck } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';

/**
 * AdminLists — one screen for the three settings-based repeating lists that
 * feed the public site:
 *   • stats                 → Home > Achievements counters
 *   • vision2030_pillars    → Home > Vision 2030 three-pillar cards
 *   • responsibility_goals  → Home > National Responsibility numbered cards
 *
 * Each list is stored as `settings.<key>.items = [ ... ]`, so the shape is
 * flat and easy to reason about.
 */
const inputCls = 'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children }) => (
  <div>
    <label dir={dir} className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{label}</label>
    {children}
  </div>
);

// ---------------- Section wrapper ----------------
const ListSection = ({ title, subtitle, Icon, children, testId }) => (
  <section
    data-testid={testId}
    className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4"
  >
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue/15 to-brand-green/15 border border-brand-blue/25 flex items-center justify-center">
        <Icon className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
      </span>
      <div className="flex-1">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

// Generic repeating-list editor. `fields` is an array of { key, labelAr, labelEn, dir?, type? }
const RepeatingList = ({ items, setItems, fields, testId, isAr, maxItems = 20 }) => {
  const addItem = () => {
    if (items.length >= maxItems) return;
    const blank = {};
    fields.forEach((f) => { blank[f.key] = f.type === 'number' ? 0 : ''; });
    setItems([...items, blank]);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, key, val) => {
    const next = items.slice();
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  };

  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {isAr ? 'لا توجد عناصر — أضف واحداً لبدء التخصيص، أو اترك القائمة فارغة لاستخدام القيم الافتراضية للموقع.' : 'No items — add one to start customising, or leave the list empty to use the site defaults.'}
        </p>
      )}
      {items.map((it, idx) => (
        <div
          key={idx}
          data-testid={`${testId}-item-${idx}`}
          className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-4 ${isAr ? 'text-right' : 'text-left'}`}
        >
          <div className="grid md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <Field key={f.key} label={isAr ? f.labelAr : f.labelEn} dir={f.dir}>
                <input
                  type={f.type || 'text'}
                  dir={f.dir}
                  className={inputCls}
                  value={it[f.key] ?? ''}
                  onChange={(e) => updateItem(idx, f.key, f.type === 'number' ? Number(e.target.value) || 0 : e.target.value)}
                  placeholder={f.placeholder || ''}
                />
              </Field>
            ))}
          </div>
          <div className={`mt-3 flex ${isAr ? 'justify-start' : 'justify-end'}`}>
            <button
              type="button"
              onClick={() => removeItem(idx)}
              data-testid={`${testId}-remove-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-xs font-semibold"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {isAr ? 'حذف' : 'Remove'}
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        data-testid={`${testId}-add`}
        disabled={items.length >= maxItems}
        className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 hover:border-brand-blue hover:text-brand-blue-700 disabled:opacity-50"
      >
        <Plus className="inline w-4 h-4 mr-1.5" />
        {isAr ? 'إضافة عنصر' : 'Add item'}
      </button>
    </div>
  );
};

// ---------------- Main page ----------------
const AdminLists = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [stats, setStats] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, p, g] = await Promise.all([
        adminApi.get('/settings/stats'),
        adminApi.get('/settings/vision2030_pillars'),
        adminApi.get('/settings/responsibility_goals'),
      ]);
      setStats(Array.isArray(s.data?.value?.items) ? s.data.value.items : []);
      setPillars(Array.isArray(p.data?.value?.items) ? p.data.value.items : []);
      setGoals(Array.isArray(g.data?.value?.items) ? g.data.value.items : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const saveKey = async (key, items) => {
    setSavingKey(key); setError(''); setMsg('');
    try {
      await adminApi.put(`/settings/${key}`, { value: { items } });
      setMsg(isAr ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSavingKey(''); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-lists-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'الإحصاءات والقوائم' : 'Stats & Lists'}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isAr
            ? 'تحكّم في عدادات الإنجازات، وركائز رؤية 2030، وأهداف المسؤولية الوطنية. إذا تركت القائمة فارغة يستخدم الموقع القيم الافتراضية.'
            : 'Control the achievements counters, Vision 2030 pillars, and National Responsibility goals. An empty list means the site uses its built-in defaults.'}
        </p>
      </div>

      {error && <div className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">{error}</div>}
      {msg && <div className="px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">{msg}</div>}

      {/* ============ STATS ============ */}
      <ListSection
        Icon={BarChart3}
        title={isAr ? 'الإحصاءات (Home › إنجازاتنا)' : 'Stats (Home › Our Achievements)'}
        subtitle={isAr ? '3 بطاقات عدّاد. الأرقام تعدّ تصاعدياً عند ظهور القسم.' : '3 counter cards. Numbers animate up when the section scrolls into view.'}
        testId="admin-lists-stats"
      >
        <RepeatingList
          items={stats}
          setItems={setStats}
          isAr={isAr}
          testId="stats"
          maxItems={6}
          fields={[
            { key: 'number',    labelAr: 'الرقم',            labelEn: 'Number',            type: 'number' },
            { key: 'suffix',    labelAr: 'اللاحقة (مثل +، %)', labelEn: 'Suffix (e.g. +, %)', dir: 'ltr' },
            { key: 'prefix_ar', labelAr: 'البادئة (عربي)',    labelEn: 'Prefix (Arabic)',   dir: 'rtl' },
            { key: 'prefix_en', labelAr: 'البادئة (إنجليزي)', labelEn: 'Prefix (English)',  dir: 'ltr' },
            { key: 'label_ar',  labelAr: 'التسمية (عربي)',    labelEn: 'Label (Arabic)',    dir: 'rtl' },
            { key: 'label_en',  labelAr: 'التسمية (إنجليزي)', labelEn: 'Label (English)',   dir: 'ltr' },
          ]}
        />
        <SaveButton onClick={() => saveKey('stats', stats)} saving={savingKey === 'stats'} isAr={isAr} testId="save-stats" />
      </ListSection>

      {/* ============ VISION 2030 PILLARS ============ */}
      <ListSection
        Icon={Sparkles}
        title={isAr ? 'ركائز رؤية 2030' : 'Vision 2030 Pillars'}
        subtitle={isAr ? '3 بطاقات. اسم الأيقونة من: Leaf, TrendingUp, Globe2, Sparkles.' : '3 cards. Icon must be one of: Leaf, TrendingUp, Globe2, Sparkles.'}
        testId="admin-lists-vision2030"
      >
        <RepeatingList
          items={pillars}
          setItems={setPillars}
          isAr={isAr}
          testId="pillars"
          maxItems={3}
          fields={[
            { key: 'icon',            labelAr: 'الأيقونة',           labelEn: 'Icon (name)',           dir: 'ltr', placeholder: 'Leaf' },
            { key: 'gradient',        labelAr: 'التدرج (Tailwind)',  labelEn: 'Gradient (Tailwind)',   dir: 'ltr', placeholder: 'from-emerald-400 via-teal-400 to-cyan-400' },
            { key: 'title_ar',        labelAr: 'العنوان (عربي)',      labelEn: 'Title (Arabic)',        dir: 'rtl' },
            { key: 'title_en',        labelAr: 'العنوان (إنجليزي)',   labelEn: 'Title (English)',       dir: 'ltr' },
            { key: 'contribution_ar', labelAr: 'المساهمة (عربي)',     labelEn: 'Contribution (Arabic)', dir: 'rtl' },
            { key: 'contribution_en', labelAr: 'المساهمة (إنجليزي)',  labelEn: 'Contribution (English)',dir: 'ltr' },
            { key: 'description_ar',  labelAr: 'الوصف (عربي)',        labelEn: 'Description (Arabic)',  dir: 'rtl' },
            { key: 'description_en',  labelAr: 'الوصف (إنجليزي)',     labelEn: 'Description (English)', dir: 'ltr' },
          ]}
        />
        <SaveButton onClick={() => saveKey('vision2030_pillars', pillars)} saving={savingKey === 'vision2030_pillars'} isAr={isAr} testId="save-pillars" />
      </ListSection>

      {/* ============ RESPONSIBILITY GOALS ============ */}
      <ListSection
        Icon={ShieldCheck}
        title={isAr ? 'أهداف المسؤولية الوطنية' : 'National Responsibility Goals'}
        subtitle={isAr ? '5 أهداف. اسم الأيقونة من: ShieldCheck, HeartPulse, TreePine, Map, Megaphone, Sparkles.' : '5 goals. Icon must be one of: ShieldCheck, HeartPulse, TreePine, Map, Megaphone, Sparkles.'}
        testId="admin-lists-responsibility"
      >
        <RepeatingList
          items={goals}
          setItems={setGoals}
          isAr={isAr}
          testId="goals"
          maxItems={8}
          fields={[
            { key: 'icon',     labelAr: 'الأيقونة',           labelEn: 'Icon (name)',        dir: 'ltr', placeholder: 'ShieldCheck' },
            { key: 'gradient', labelAr: 'التدرج (Tailwind)',  labelEn: 'Gradient (Tailwind)',dir: 'ltr' },
            { key: 'ar',       labelAr: 'النص (عربي)',         labelEn: 'Text (Arabic)',      dir: 'rtl' },
            { key: 'en',       labelAr: 'النص (إنجليزي)',      labelEn: 'Text (English)',     dir: 'ltr' },
          ]}
        />
        <SaveButton onClick={() => saveKey('responsibility_goals', goals)} saving={savingKey === 'responsibility_goals'} isAr={isAr} testId="save-goals" />
      </ListSection>
    </div>
  );
};

const SaveButton = ({ onClick, saving, isAr, testId }) => (
  <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={saving}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      {isAr ? 'حفظ' : 'Save'}
    </button>
  </div>
);

export default AdminLists;
